<template>
  <video-player
    class="hotspot-video"
    :image="image"
    :intersection-observer="{
      enable: true,
    }"
    :lazyload="lazyload"
    :parent-state="commandState"
    :sources="video.sources"
    :subtitles="subtitles"
    @video-end="handleVideoEnd"
    @video-state-update="(payload) => state = payload"
    @video-time-update="(payload) => time = payload"
  />
</template>

<script>

/**
 * Vue: Hotspot video (hotspot)
 * -----------------------------------------------------------------------------
 * Hotspot video player functionality.
 *
 * @param {String} activePoiId - Active point of interest ID.
 * @param {Array} formattedPoi - Formatted points of interest.
 * @param {Object} [image] - Shopify image object.
 * @param {Boolean} [lazyload] - Lazyload image, defaults to true.
 * @param {String} [subtitles] - Video subtitles URL.
 * @param {Object} video - Formatted media video object.
 *
 */
import VideoPlayer from '~async/video-player/video-player'

export default {
  name: 'HotspotVideo',

  components: {
    VideoPlayer,
  },

  props: {
    activePoiId: {
      type: String,
      default: '',
    },
    formattedPoi: {
      type: Array,
      default: () => [],
    },
    image: {
      type: [Object],
      default: () => ({
        alt: '',
        height: 0,
        src: '',
        width: 0,
      }),
    },
    lazyload: {
      type: Boolean,
      default: true,
    },
    subtitles: {
      type: String,
      default: '',
    },
    video: {
      type: Object,
      default: () => ({
        format: 'mp4',
        mimeType: 'video/mp4',
        url: '',
      }),
      required: true,
    },
  },

  emits: {
    'update-active-poi-id': (payload) => typeof payload === 'string',
    'update-visible-poi': (payload) => Array.isArray(payload),
  },

  data() {
    return {
      commandState: '',
      state: 'loading',
      time: 0,
      visiblePoi: [],
      wasPlaying: false,
    }
  },

  watch: {

    /**
     * Watch active POI ID to pause/play video.
     * - Set was playing state so video resumes when closing modal.
     */
    activePoiId() {
      if (!this.activePoiId) {
        if (this.wasPlaying) {
          this.commandState = 'playing'
          this.wasPlaying = false
        }

        return
      }

      if (this.state !== 'playing') {
        return
      }

      this.commandState = 'paused'
      this.wasPlaying = true
    },

    /**
     * Watch video playback state to update active POI.
     */
    state() {
      if (this.state !== 'playing') {
        return
      }

      this.$emit('update-active-poi-id', '')
      this.getVisiblePoi()
    },

    /**
     * Watch video time to update playback state and active POI.
     */
    time() {
      this.getVisiblePoi()
    },

    /**
     * Watch visible POI to emit event to update parent component.
     */
    visiblePoi() {
      this.$emit('update-visible-poi', this.visiblePoi)
    },
  },

  mounted() {
    this.getVisiblePoi()
  },

  methods: {

    /**
     * Get visible points of interest.
     */
    getVisiblePoi() {
      if (this.showOverlay) {
        this.visiblePoi = []
        return
      }

      /**
       * Determine if point of interest should be shown based on current video
       * timestamp.
       */
      this.visiblePoi = this.formattedPoi.map((poi) => {
        const startBeforeCurrentTime = poi.timings.start <= this.time
        const endAfterCurrentTime = !poi.timings.end || poi.timings.end >= this.time

        if (!startBeforeCurrentTime || !endAfterCurrentTime) {
          return false
        }

        return poi.id
      }).filter(Boolean)
    },

    /**
     * Get POI timings.
     * - Used in parent component to format POI.
     * @param {Object} poi - Current point of interest.
     * @returns {Object}
     */
    getPoiTiming(poi) {
      if (!this.video) {
        return poi
      }

      let end = 0
      let start = 0

      if (poi.video.end) {
        end = (Number(poi.video.end.split(':')[0]) * 60) + Number(poi.video.end.split(':')[1])
      }

      if (poi.video.start) {
        start = (Number(poi.video.start.split(':')[0]) * 60) + Number(poi.video.start.split(':')[1])
      }

      return {
        ...poi,
        timings: {
          end,
          start,
        },
      }
    },

    /**
     * Handle video playback end.
     */
    handleVideoEnd() {
      this.getVisiblePoi()
    },
  },
}
</script>
